<script setup lang="ts">
import { keys } from "~/utilities/injection"

defineProps({
  firstAudioArticleUrl: {
    type: String as PropType<string>,
    default: () => null,
  },
})
const isDark = inject(keys.isDark)
const route = useRoute()
const router = useRouter()
const { isAuthorized } = useAuth()

const { activeArticle } = storeToRefs(useProductContentStore())

const store = useAudioStore()

const { playlist, activePlayer, currentTrack, isPlaying, tts } = storeToRefs(store)

const isLoading = computed(() => tts.value.status === "loading")

const showAudioPlayer = computed(() => playlist.value?.some(track => track.id === activeArticle.value?.id))

const disableAudio = computed(() => currentTrack.value?.textToSpeech && !isAuthorized.value)

const redirectUrl = computed(() => router.resolve({
  path: useLoginPageUrl().value,
  query: { fromPath: encodeURIComponent(route.fullPath) },
}).href)

function handleTogglePlayer() {
  activePlayer.value = true
  store.togglePlayState()
}

const isSmallScreen = useMediaQuery("(max-width: 744px)")
</script>

<template>
  <div v-if="showAudioPlayer" class="flex flex-col max-w-[340px] gap-2">
    <div class="flex gap-3 text-[--theme-darker]" :class="{ 'text-gray-500': disableAudio }">
      <UButtonNew
        :size="isSmallScreen ? 'xs' : 'sm'"
        :disabled="disableAudio"
        :aria-label="(isPlaying || isLoading) ? 'Sett på pause' : 'Lytt til artikkelen'"
        :theme="isDark ? 'light' : 'medium'"
        circle
        :data-pendo="'audio-player' + (tts.status !== 'disabled' ? '-tts' : '')"
        @click="handleTogglePlayer"
      >
        <UIcon :size="isSmallScreen ? 'xs' : 'md'" :name="isPlaying ? 'pause' : 'play-filled'" />
      </UButtonNew>
      <p
        class="!m-0 self-center font-national2 text-label-md font-extrabold"
        :class="{
          'text-[--theme-darker]': !isDark,
          'text-white': isDark,
          'text-grey-60': disableAudio,
        }"
      >
        <span v-if="isPlaying">
          Pause
        </span>
        <span v-else>
          Lytt til {{ firstAudioArticleUrl ? 'kapittelet' : 'artikkelen' }}
        </span>
      </p>
    </div>
    <p
      v-if="currentTrack?.textToSpeech"
      class="text-text-xxs italic font-numeraWebRegular"
      :class="{ 'text-white': isDark }"
    >
      Stemmen som leser denne artikkelen er laget ved hjelp av kunstig intelligens (KI).
      <a v-if="disableAudio" data-pendo="audio-player-login" class="!inline font-bold !no-underline not-italic" :href="redirectUrl">
        Logg inn <span class="font-normal">for å lytte.</span>
      </a>
    </p>
  </div>
</template>
